/**
 * Beat-grid timing for the piano roll and timeline (4/4 assumed for grid labels).
 * Internal storage: beats as floating point (triplets use 1/3, 2/3, etc.).
 *
 * Legacy encoding: `gridSize` is chosen from GRID_DIVISION_OPTIONS; step in beats = 4 / gridSize.
 */

/**
 * @param {number} gridSize - from GRID_DIVISION_OPTIONS (1,2,4,6,8,12,16,24,32,48,64,…)
 * @returns {number} beat length of one grid cell
 */
export function getGridStepBeatsFromGridSize(gridSize) {
    const gs = Number(gridSize);
    if (!Number.isFinite(gs) || gs <= 0) return 0.25;
    return 4 / gs;
}

/**
 * Snap a beat position to the nearest grid line.
 * @param {number} beats
 * @param {number} gridStepBeats
 */
export function snapBeatToGridStep(beats, gridStepBeats) {
    if (!(gridStepBeats > 0) || !Number.isFinite(beats)) return 0;
    return Math.round(beats / gridStepBeats) * gridStepBeats;
}

/** Integer cell index for piano-roll keys and drag (nearest line). */
export function beatToGridIndex(beats, gridStepBeats) {
    if (!(gridStepBeats > 0)) return 0;
    return Math.round(beats / gridStepBeats);
}

export function gridIndexToBeat(gridIndex, gridStepBeats) {
    return gridIndex * gridStepBeats;
}

/**
 * Approximate "divisions per beat" for ruler / adaptive UI that expects integers.
 * Triplet gridSizes map to the straight subdivision density that looks closest.
 */
export function approximateDivisionsPerBeatForRuler(gridSize) {
    const s = Number(gridSize);
    if (s === 6) return 3;
    if (s === 12) return 6;
    if (s === 24) return 12;
    if (s === 48) return 24;
    return Math.max(1, Math.round(s / 4));
}

const TRIPLET_EPS = 1e-5;

/**
 * A tuplet quantize value is the step between adjacent partials (e.g. 2/3 for quarter triplets).
 * Return { periodBeats, stepBeats } where 3 * stepBeats === periodBeats, or null if not a known triplet.
 *
 * Quarter-note triplet: 3 notes in 2 beats → step 2/3.
 * Eighth-note triplet: 3 notes in 1 beat → step 1/3.
 * Sixteenth-note triplet: 3 notes in 1/2 beat → step 1/6.
 * Thirty-second triplet: 3 notes in 1/4 beat → step 1/12.
 */
export function getTripletQuantizeSpec(quantizeStepBeats) {
    const v = Number(quantizeStepBeats);
    if (!Number.isFinite(v)) return null;
    if (Math.abs(v - 2 / 3) < TRIPLET_EPS) return { periodBeats: 2, stepBeats: 2 / 3 };
    if (Math.abs(v - 1 / 3) < TRIPLET_EPS) return { periodBeats: 1, stepBeats: 1 / 3 };
    if (Math.abs(v - 1 / 6) < TRIPLET_EPS) return { periodBeats: 0.5, stepBeats: 1 / 6 };
    if (Math.abs(v - 1 / 12) < TRIPLET_EPS) return { periodBeats: 0.25, stepBeats: 1 / 12 };
    return null;
}

/**
 * Assign consecutive triplet partials (0, step, 2*step) within each period window, chunked in groups of 3
 * by sort order. Fixes quarter-triplet quantize: three quarter notes at 0,1,2 become 0, 2/3, 4/3 — not 0, 4/3, 2.
 *
 * @param {number[]} sortedStarts - note start times in beats, ascending
 * @param {number} periodBeats - length of one complete triplet group in time (e.g. 2 for quarter triplets)
 * @returns {number[]} target start for each note (same length)
 */
export function assignTripletGroupTargets(sortedStarts, periodBeats) {
    const period = Number(periodBeats);
    if (!Number.isFinite(period) || period <= 0 || sortedStarts.length === 0) return [...sortedStarts];
    const step = period / 3;
    const n = sortedStarts.length;
    const out = new Array(n);
    let i = 0;
    while (i < n) {
        const windowStart = Math.floor(sortedStarts[i] / period) * period;
        for (let j = 0; j < 3 && i < n; j += 1, i += 1) {
            out[i] = windowStart + j * step;
        }
    }
    return out;
}

/**
 * Nearest valid triplet onset (any partial, any period) — for single-note triplet quantize.
 */
export function snapSingleToTripletOnset(t, periodBeats) {
    const period = Number(periodBeats);
    if (!Number.isFinite(period) || period <= 0) return t;
    const step = period / 3;
    const ws = Math.floor(t / period) * period;
    let best = ws;
    let bestD = Infinity;
    for (let k = -2; k <= 2; k += 1) {
        const base = ws + k * period;
        for (let j = 0; j < 3; j += 1) {
            const cand = base + j * step;
            if (cand < -1e-9) continue;
            const d = Math.abs(t - cand);
            if (d < bestD - 1e-12) {
                bestD = d;
                best = cand;
            }
        }
    }
    return best;
}
